import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CircleNotch, CheckCircle, Eye, EyeSlash, Warning } from 'phosphor-react';

// Vertical logo (brain + GENI)
const LOGO_VERTICAL = "https://customer-assets.emergentagent.com/job_chart-analyzer-107/artifacts/zc7oaxip_brain%20%2B%20GENICERTO.png";

export default function ResetPassword() {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [checking, setChecking] = useState(true);
  const [validSession, setValidSession] = useState(false);
  const [sessionError, setSessionError] = useState('');
  const navigate = useNavigate();

  // Check if user has a valid recovery session (came from reset link)
  useEffect(() => {
    let mounted = true;

    const checkRecoverySession = async () => {
      try {
        // Listen for auth state changes - Supabase processes the URL hash automatically
        const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
          if (!mounted) return;

          if (event === 'PASSWORD_RECOVERY') {
            // Valid recovery session from email link
            setValidSession(true);
            setChecking(false);
          } else if (event === 'SIGNED_IN' && session) {
            // User already signed in (possibly from recovery)
            setValidSession(true);
            setChecking(false);
          }
        });

        // Also check for existing session (in case auth state already changed)
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          throw error;
        }

        if (session) {
          if (mounted) {
            setValidSession(true);
            setChecking(false);
          }
        } else {
          // Wait a moment for auth state change, then show error if still no session
          setTimeout(() => {
            if (mounted && !validSession) {
              setSessionError('This password reset link is invalid or has expired.');
              setChecking(false);
            }
          }, 2000);
        }

        return () => {
          subscription.unsubscribe();
        };
      } catch (err) {
        console.error('Recovery session check error:', err);
        if (mounted) {
          setSessionError('Unable to verify reset link. Please request a new one.');
          setChecking(false);
        }
      }
    };

    checkRecoverySession();

    return () => {
      mounted = false;
    };
  }, [validSession]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate passwords match
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    // Validate password length
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      const { error } = await supabase.auth.updateUser({
        password: password
      });

      if (error) {
        throw error;
      }

      // Success - redirect to login with success message
      navigate('/login?reset=success', { replace: true });
    } catch (err) {
      console.error('Update password error:', err);
      setError(err.message || 'Failed to update password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="text-center">
          <CircleNotch size={32} className="animate-spin text-white/60 mx-auto mb-2" />
          <p className="text-white/40 text-sm">Verifying reset link...</p>
        </div>
      </div>
    );
  }

  // Show error if session is invalid or expired
  if (sessionError || !validSession) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-4">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="flex flex-col items-center justify-center mb-8">
            <img
              src={LOGO_VERTICAL}
              alt="GENI"
              className="h-20 md:h-24 lg:h-28 w-auto object-contain mx-auto"
            />
          </div>

          {/* Error Message */}
          <div className="bg-[#121212] border border-white/10 rounded-lg p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-[#FF3B30]/10 flex items-center justify-center mx-auto mb-4">
              <Warning size={32} weight="duotone" className="text-[#FF3B30]" />
            </div>
            <h2 className="text-xl font-bold text-white mb-2">Invalid or expired link</h2>
            <p className="text-white/60 text-sm mb-6">
              {sessionError || 'This password reset link is no longer valid. Please request a new one.'}
            </p>
            <div className="flex flex-col gap-3">
              <Button
                onClick={() => navigate('/forgot-password')}
                className="w-full bg-white text-black font-bold hover:bg-gray-200"
              >
                Request new reset link
              </Button>
              <button
                onClick={() => navigate('/login')}
                className="text-white/50 hover:text-white/80 text-sm transition-colors"
              >
                Back to login
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex flex-col items-center justify-center mb-8">
          <img
            src={LOGO_VERTICAL}
            alt="GENI"
            className="h-20 md:h-24 lg:h-28 w-auto object-contain mx-auto"
          />
        </div>

        {/* Form Container */}
        <div className="bg-[#121212] border border-white/10 rounded-lg p-8">
          <h1 className="text-2xl font-bold text-white mb-2">Set new password</h1>
          <p className="text-white/60 text-sm mb-6">
            Enter your new password below.
          </p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label htmlFor="password" className="text-white/80 mb-2 block">New Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  disabled={loading}
                  className="bg-[#0A0A0A] border-white/10 text-white pr-10"
                  data-testid="reset-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/60"
                >
                  {showPassword ? <EyeSlash size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div>
              <Label htmlFor="confirmPassword" className="text-white/80 mb-2 block">Confirm Password</Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  disabled={loading}
                  className="bg-[#0A0A0A] border-white/10 text-white pr-10"
                  data-testid="reset-confirm-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/60"
                >
                  {showConfirmPassword ? <EyeSlash size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            {/* Password match indicator */}
            {confirmPassword && (
              <div className={`flex items-center gap-2 text-sm ${password === confirmPassword ? 'text-[#00E676]' : 'text-[#FF3B30]'}`}>
                <CheckCircle size={16} weight={password === confirmPassword ? 'fill' : 'regular'} />
                <span>{password === confirmPassword ? 'Passwords match' : 'Passwords do not match'}</span>
              </div>
            )}

            {error && (
              <div className="bg-[#FF3B30]/10 border border-[#FF3B30]/30 rounded-sm p-3">
                <p className="text-[#FF3B30] text-sm">{error}</p>
              </div>
            )}

            <Button
              type="submit"
              disabled={loading || !password || !confirmPassword}
              className="w-full bg-white text-black font-bold hover:bg-gray-200 transition-colors rounded-sm disabled:opacity-50"
              data-testid="reset-password-submit"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <CircleNotch size={18} className="animate-spin" />
                  Updating...
                </span>
              ) : (
                'Update password'
              )}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
